// Canvas API Types
export interface PlannerNote {
	id: number;
	title: string;
	description: string;
	user_id: number;
	workflow_state: string;
	course_id?: number;
	todo_date: string;
	linked_object_type?: string;
	linked_object_id?: number;
	linked_object_html_url?: string;
	linked_object_url?: string;
}

export interface PlannerOverride {
	id: number;
	plannable_type: string;
	plannable_id: number;
	user_id: number;
	assignment_id?: number;
	workflow_state: string;
	marked_complete: boolean;
	dismissed: boolean;
	created_at: string;
	updated_at: string;
	deleted_at?: string;
}

export interface AssignmentSubmissionStatus {
	submitted: boolean;
	excused: boolean;
	graded: boolean;
	posted_at?: string;
	late: boolean;
	missing: boolean;
	needs_grading: boolean;
	has_feedback: boolean;
	redo_request: boolean;
	feedback?: {
		comment: string;
		is_media: boolean;
		author_name: string;
		author_avatar_url: string;
	};
}

export interface PlannerItem {
	context_type: string;
	course_id?: number;
	planner_override?: PlannerOverride;
	submissions: AssignmentSubmissionStatus | false;
	plannable_id: string;
	plannable_type: string;
	plannable_date: string;
	plannable: {
		id: number;
		title: string;
		created_at: string;
		updated_at: string;
		points_possible?: number;
		due_at?: string;
		[key: string]: any;
	};
	html_url: string;
	context_name?: string;
	context_image?: string;
	new_activity?: boolean;
}

export interface GetNextAssignmentsOptions {
	count: number;
	startDate?: Date;
	endDate?: Date;
	courseIds?: number[];
	excludeCompleted?: boolean;
	excludeDismissed?: boolean;
	plannableTypes?: string[];
	sortOrder?: "asc" | "desc";
}
// Based on official Canvas API documentation and response objects

export interface CanvasSubmission {
  id: number;
  body: string | null;
  url: string | null;
  grade: string | null;
  score: number | null;
  submitted_at: string | null;
  assignment_id: number;
  user_id: number;
  submission_type: string | null;
  workflow_state: string;
  grade_matches_current_submission: boolean;
  graded_at: string | null;
  grader_id: number | null;
  attempt: number | null;
  cached_due_date: string | null;
  excused: boolean;
  late_policy_status: string | null;
  points_deducted: number | null;
  grading_period_id: number | null;
  extra_attempts: number | null;
  posted_at: string | null;
  redo_request: boolean;
  custom_grade_status_id: number | null;
  sticker: string | null;
  late: boolean;
  missing: boolean;
  seconds_late: number;
  entered_grade: string | null;
  entered_score: number | null;
  preview_url: string | null;
  assignment_visible?: boolean;
}

export interface CanvasAssignment {
  id: number;
  description: string;
  due_at: string | null;
  unlock_at: string | null;
  lock_at: string | null;
  points_possible: number;
  grading_type: string;
  assignment_group_id: number;
  grading_standard_id: number | null;
  created_at: string;
  updated_at: string;
  peer_reviews: boolean;
  automatic_peer_reviews: boolean;
  position: number;
  grade_group_students_individually: boolean;
  anonymous_peer_reviews: boolean;
  group_category_id: number | null;
  post_to_sis: boolean;
  moderated_grading: boolean;
  omit_from_final_grade: boolean;
  intra_group_peer_reviews: boolean;
  anonymous_instructor_annotations: boolean;
  anonymous_grading: boolean;
  graders_anonymous_to_graders: boolean;
  grader_count: number;
  grader_comments_visible_to_graders: boolean;
  final_grader_id: number | null;
  grader_names_visible_to_final_grader: boolean;
  allowed_attempts: number;
  annotatable_attachment_id: number | null;
  hide_in_gradebook: boolean;
  suppress_assignment: boolean;
  secure_params: string | null;
  lti_context_id: string | null;
  course_id: number;
  name: string;
  submission_types: string[];
  has_submitted_submissions: boolean;
  due_date_required: boolean;
  max_name_length: number;
  in_closed_grading_period: boolean;
  graded_submissions_exist: boolean;
  is_quiz_assignment: boolean;
  can_duplicate: boolean;
  original_course_id: number | null;
  original_assignment_id: number | null;
  original_lti_resource_link_id: string | null;
  original_assignment_name: string | null;
  original_quiz_id: number | null;
  workflow_state: string;
  important_dates: boolean;
  muted: boolean;
  html_url: string;
  published: boolean;
  only_visible_to_overrides: boolean;
  visible_to_everyone: boolean;
  submission?: CanvasSubmission;
  locked_for_user: boolean;
  submissions_download_url: string;
  post_manually: boolean;
  anonymize_students: boolean;
  require_lockdown_browser: boolean;
  restrict_quantitative_data: boolean;
}

export interface CanvasEnrollment {
  id: number;
  course_id: number;
  sis_course_id: string | null;
  course_integration_id: string | null;
  course_section_id: number;
  section_integration_id: string | null;
  sis_account_id: string | null;
  sis_section_id: string | null;
  enrollment_state: string;
  limit_privileges_to_course_section: boolean;
  sis_user_id: string | null;
  user_id: number;
  type: string;
  role: string;
  role_id: number;
  created_at: string;
  updated_at: string;
  start_at: string | null;
  end_at: string | null;
  last_activity_at: string | null;
  last_attended_at: string | null;
  total_activity_time: number;
  html_url: string;
  grades?: {
    html_url: string;
    current_score: number | null;
    current_grade: string | null;
    final_score: number | null;
    final_grade: string | null;
    unposted_current_score: number | null;
    unposted_current_grade: string | null;
    unposted_final_score: number | null;
    unposted_final_grade: string | null;
  };
  user?: {
    id: number;
    name: string;
    sortable_name: string;
    short_name: string;
    sis_user_id: string | null;
    integration_id: string | null;
    login_id: string;
    avatar_url: string | null;
    enrollments?: CanvasEnrollment[];
    email: string | null;
    locale: string | null;
    effective_locale: string | null;
    last_login: string | null;
    time_zone: string | null;
    bio: string | null;
  };
  sis_import_id: number | null;
  root_account_id: number;
  associated_user_id: number | null;
  course_section_integration_id: string | null;
}

export interface CanvasGradingPeriod {
  id: number;
  title: string;
  start_date: string;
  end_date: string;
  close_date: string;
  weight: number | null;
  is_closed: boolean;
  is_last: boolean;
}

export interface CanvasCourse {
  id: number;
  sis_course_id: string | null;
  uuid: string;
  integration_id: string | null;
  sis_import_id: number | null;
  name: string;
  course_code: string;
  original_name?: string;
  workflow_state: string;
  account_id: number;
  root_account_id: number;
  enrollment_term_id: number;
  grading_periods?: CanvasGradingPeriod[] | null;
  grading_standard_id: number | null;
  grade_passback_setting: string | null;
  created_at: string;
  start_at: string | null;
  end_at: string | null;
  locale: string | null;
  enrollments?: CanvasEnrollment[] | null;
  total_students?: number;
  calendar?: any;
  default_view: string;
  syllabus_body?: string;
  needs_grading_count?: number;
  term?: any;
  course_progress?: any;
  apply_assignment_group_weights: boolean;
  permissions?: Record<string, boolean>;
  is_public: boolean;
  is_public_to_auth_users: boolean;
  public_syllabus: boolean;
  public_syllabus_to_auth: boolean;
  public_description?: string;
  storage_quota_mb: number;
  storage_quota_used_mb: number;
  hide_final_grades: boolean;
  license?: string;
  allow_student_assignment_edits: boolean;
  allow_wiki_comments: boolean;
  allow_student_forum_attachments: boolean;
  open_enrollment: boolean;
  self_enrollment: boolean;
  restrict_enrollments_to_course_dates: boolean;
  course_format: string;
  access_restricted_by_date?: boolean;
  time_zone: string;
  blueprint?: boolean;
  blueprint_restrictions?: Record<string, boolean>;
  blueprint_restrictions_by_object_type?: Record<string, Record<string, boolean>>;
  template?: boolean;
  color?: string;
}

export const defaultTaskValues: inputTypes = {
  "id": null,
  "title": "",
  "description": "",
  "type": "assignment",
  "course": null,
  "due": new Date().toISOString(),
  "points": null,
  "url": null,
  "duration": 30,
  "plannedAt": null,
  "status": "incomplete",
  "score": null,
  "priority": null,
}

export type AssignmentDetails = {
  id: number | null,
  title: string,
  description: string,
  type: string,
  course: string | null,
  due: string | null,
  points: number | null,
  url: string | null,
  duration: number,
  plannedAt: string | null,
  status: string,
  score: number | null,
  priority: number | null,
}

export type AssignmentWithOriginals = {
  "original": AssignmentDetails,
  "current": AssignmentDetails,
}


export interface AssignmentPlanned {
    id: number;
    course: number;
    title: string;
    duration: number;
    plannedAt: string;
    status: string;
}

export type ChangeLogItem = {
    id: number;
    table_name: string;
    operation: "INSERT" | "DELETE" | "UPDATE";
    updated_at: string;
    record_id: string;
    user_id: string;
    metadata: Record<string, any>;
}

export type NoteCountCache = Record<string, number>;
export type ThemeLikeCache = Record<string, boolean>;
export type PlannedAssignmentCache = Record<string, AssignmentPlanned>;
export type RecentChangeLogIds = Set<number>;

export const tableNameMap: Record<string, string> = {
    "note_counts": "cached_note_counts",
    "theme_likes": "cached_theme_likes",
    "planned_assignments": "cached_planned_assignments",
}